// One case's checks — what makes an answer right. Used by the case editor
// and by the import review.
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CHECK_TYPES, lines } from './format';

const listField = (value) => (Array.isArray(value) ? value.join('\n') : value || '');

function ValueCheck({ check, set, metrics }) {
  const live = check.mode ? check.mode === 'live' : Boolean(check.metric);
  const metric = metrics.find((m) => m.key === check.metric);
  return (
    <>
      <div className="ts-seg" role="group">
        <button type="button" className={live ? 'on' : ''} onClick={() => set({ mode: 'live' })}>Live aus dem Dashboard</button>
        <button type="button" className={!live ? 'on' : ''} onClick={() => set({ mode: 'fixed', metric: '' })}>Fester Wert</button>
      </div>
      {live ? (
        <div className="ts-row">
          <label className="ts-field grow">
            <span>Kennzahl</span>
            <select value={check.metric || ''} onChange={(e) => set({ metric: e.target.value, args: {} })}>
              <option value="">— wählen —</option>
              {['efd', 'ppi'].map((model) => (
                <optgroup key={model} label={model === 'efd' ? 'EFD-Dashboard' : 'PPI-Dashboard'}>
                  {metrics.filter((m) => m.model === model).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          {(metric?.args || []).map((a) => (
            <label key={a} className="ts-field">
              <span>{{ unit: 'Einheit (wie gespeichert)', country: 'Land', band: 'Grössenklasse', month: 'Monat (z. B. Jun 2026)', asset_class: 'Anlageklasse' }[a] || a}</span>
              <input value={(check.args || {})[a] || ''} onChange={(e) => set({ args: { ...(check.args || {}), [a]: e.target.value } })} />
            </label>
          ))}
        </div>
      ) : (
        <div className="ts-row">
          <label className="ts-field">
            <span>Erwarteter Wert</span>
            <input inputMode="decimal" value={check.expected ?? ''} placeholder="46.6"
              onChange={(e) => set({ expected: e.target.value.replace(/'/g, '').replace(',', '.') })} />
          </label>
          <label className="ts-field">
            <span>± Toleranz</span>
            <input inputMode="decimal" value={check.tolerance ?? ''} placeholder="z. B. 50000 für „11,9M“"
              onChange={(e) => set({ tolerance: e.target.value.replace(/'/g, '').replace(',', '.') })} />
          </label>
          <label className="ts-field">
            <span>Gilt für Stand</span>
            <input value={check.valid_for || ''} placeholder="Aug 2026 · 2026-03-31" onChange={(e) => set({ valid_for: e.target.value })} />
          </label>
        </div>
      )}
      {live && metric && <div className="ts-hint mono">{metric.endpoint} → {metric.path}</div>}
    </>
  );
}

export default function CheckEditor({ checks, onChange, metrics }) {
  const update = (i, patch) => onChange(checks.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const remove = (i) => onChange(checks.filter((_, j) => j !== i));
  const add = () => onChange([...checks, { type: 'value', label: '', metric: '', expected: '', valid_for: '' }]);
  return (
    <div className="ts-checks">
      {checks.map((c, i) => (
        <div key={i} className="ts-check">
          <div className="ts-row">
            <label className="ts-field">
              <span>Prüfung</span>
              <select value={c.type} onChange={(e) => update(i, { type: e.target.value })}>
                {CHECK_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className="ts-field grow">
              <span>Bezeichnung</span>
              <input value={c.label || ''} placeholder="z. B. RoA Gruppe" onChange={(e) => update(i, { label: e.target.value })} />
            </label>
            <button type="button" className="ts-icon-btn danger" title="Prüfung entfernen" onClick={() => remove(i)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
          {c.type === 'value' && <ValueCheck check={c} set={(p) => update(i, p)} metrics={metrics} />}
          {(c.type === 'contains' || c.type === 'not_contains') && (
            <label className="ts-field">
              <span>{c.type === 'contains' ? 'Einer dieser Texte muss vorkommen' : 'Keiner dieser Texte darf vorkommen'} (einer pro Zeile)</span>
              <textarea rows={3} value={listField(c.type === 'contains' ? c.any : c.texts)}
                onChange={(e) => update(i, c.type === 'contains' ? { any: lines(e.target.value) } : { texts: lines(e.target.value) })} />
            </label>
          )}
          {(c.type === 'contains' || c.type === 'not_contains') && (
            <label className="ts-check-inline">
              <input type="checkbox" checked={!!c.in_tables} onChange={(e) => update(i, { in_tables: e.target.checked })} />
              nur in Tabellenzeilen prüfen (nicht im Begleittext)
            </label>
          )}
          {c.type === 'order' && (
            <label className="ts-field">
              <span>Namen in dieser Reihenfolge (einer pro Zeile)</span>
              <textarea rows={4} value={listField(c.names)} onChange={(e) => update(i, { names: lines(e.target.value) })} />
            </label>
          )}
          {c.type === 'refusal' && <div className="ts-hint">Bro muss die Frage ablehnen („kein Zugriff“, „nur dein Buch“ …).</div>}
          {c.type === 'period' && <div className="ts-hint">Die Antwort muss den aktuellen Stichtag der Daten nennen.</div>}
          {c.type === 'tool' && (
            <div className="ts-row">
              <label className="ts-field"><span>Werkzeug</span>
                <input value={c.tool || ''} placeholder="dashboard_view" onChange={(e) => update(i, { tool: e.target.value.trim() })} /></label>
              <label className="ts-field"><span>Ansicht (optional)</span>
                <input value={c.view || ''} placeholder="top_flop" onChange={(e) => update(i, { view: e.target.value.trim() })} /></label>
            </div>
          )}
        </div>
      ))}
      <button type="button" className="ts-btn ghost" onClick={add}><FontAwesomeIcon icon={faPlus} /> Prüfung hinzufügen</button>
    </div>
  );
}

/** Before saving: numbers as numbers, the UI-only "mode" resolved. */
export function cleanChecks(checks) {
  return (checks || []).map((c) => {
    const { mode, ...rest } = c;
    if (rest.type !== 'value') return rest;
    const live = mode ? mode === 'live' : Boolean(rest.metric);
    const toNumber = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const expected = toNumber(rest.expected);
    const tolerance = toNumber(rest.tolerance);
    const out = { ...rest, metric: live ? rest.metric : '', expected: Number.isFinite(expected) ? expected : null };
    if (Number.isFinite(tolerance) && tolerance > 0) out.tolerance = tolerance; else delete out.tolerance;
    return out;
  });
}
